import { m } from 'motion/react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogHero,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import {
  Table,
  TableBody,
  TableCell,
  TableCellSkeleton,
  TableColumn,
  TableHeader,
  TableRow,
  TableRowSkeleton,
} from '@/components/Table'
import type { JobScopeChangeStatus } from '@/features/jobs/job-scope-changes/JobScopeChange'
import { DEFAULT, match } from '@/utils/pattern-match-util'
import { formatMoney } from '@/utils/money-util'
import { type ReactNode } from 'react'
import cx from 'classnames'
import styles from './JobScopeChangeDialog.module.css'
import { dateFormatters } from '@/utils/date-util'
import { AutoSkeleton, SkeletonList } from '@/components/Skeleton'
import {
  formatJobScopeChangeItemPrice,
  formatJobScopeChangeItemQuantity,
} from '@/features/jobs/job-scope-changes/job-scope-change-formatters'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { ButtonGroup } from '@/components/ButtonGroup'
import { useScrollFadeMaskImage } from '@/hooks/scroll-fade'
import { isNotNil } from '@/utils/object-util'

interface JobScopeChangeDialogProps {
  /**
   * The additional amount due.
   */
  amountDue: number | null
  /**
   * The total price after changes.
   */
  price: number | null
  /**
   * Display name of the Pro.
   */
  displayName: string
  /**
   * Date when the response was recorded.
   */
  respondedAt: Date | null
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Whether the accept operation is pending.
   */
  isAcceptPending: boolean
  /**
   * Whether the decline operation is pending.
   */
  isDeclinePending: boolean
  /**
   * Whether a new billing method is needed.
   */
  isNewBillingMethodNeeded: boolean
  /**
   * Status of the job scope change.
   */
  status: JobScopeChangeStatus
  /**
   * Whether to show skeleton state.
   */
  isSkeleton: boolean
  /**
   * Content to display in the dialog.
   */
  children?: ReactNode
  /**
   * Called when the dialog is closed.
   */
  onClose: () => unknown
  /**
   * Called when changes are accepted.
   */
  onAccept: () => unknown
  /**
   * Called when changes are declined.
   */
  onDecline: () => unknown
  /**
   * Called when a new billing method is requested.
   */
  onNewBillingMethod: () => unknown
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the accept button.
   */
  acceptTestId?: string
  /**
   * Test ID attribute for the decline button.
   */
  declineTestId?: string
  /**
   * Test ID attribute for the add payment method button.
   */
  addPaymentMethodTestId?: string
  /**
   * Test ID attribute for the not now button.
   */
  notNowTestId?: string
  /**
   * Test ID attribute for the close button.
   */
  closeTestId?: string
}

/**
 * The JobScopeChangeDialog
 */
export function JobScopeChangeDialog({
  amountDue,
  price,
  displayName,
  respondedAt,
  isOpen,
  isAcceptPending,
  isDeclinePending,
  isSkeleton,
  isNewBillingMethodNeeded,
  status,
  children,
  onClose,
  onAccept,
  onDecline,
  onNewBillingMethod,
  testId,
  acceptTestId,
  declineTestId,
  addPaymentMethodTestId,
  notNowTestId,
  closeTestId,
}: JobScopeChangeDialogProps) {
  const { hero, header } = match(status, {
    PendingClientApproval: {
      hero: (
        <PendingApprovalHero amountDue={amountDue} isSkeleton={isSkeleton} />
      ),
      header: <PendingApprovalHeader price={price} isSkeleton={isSkeleton} />,
    },

    Rejected: {
      hero: <RejectedHero amountDue={amountDue} isSkeleton={isSkeleton} />,
      header: (
        <RejectedHeader
          displayName={displayName}
          respondedAt={respondedAt}
          isSkeleton={isSkeleton}
        />
      ),
    },

    Accepted: {
      hero: <ApprovedHero amountDue={amountDue} isSkeleton={isSkeleton} />,
      header: (
        <ApprovedHeader
          displayName={displayName}
          respondedAt={respondedAt}
          isSkeleton={isSkeleton}
        />
      ),
    },
    [DEFAULT]: {
      hero: (
        <PendingApprovalHero amountDue={amountDue} isSkeleton={isSkeleton} />
      ),
      header: <PendingApprovalHeader price={price} isSkeleton={isSkeleton} />,
    },
  })

  return (
    <ModalDialog
      gap="none"
      size="base"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isAcceptPending && !isDeclinePending}
      hero={hero}
      testId={testId}
    >
      <JobScopeChangeDialogContent header={header}>
        {children}
      </JobScopeChangeDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          {status === 'PendingClientApproval' && (
            <>
              {isNewBillingMethodNeeded ? (
                <Button
                  label="Add payment method"
                  variant="default"
                  isSkeleton={isSkeleton}
                  isDisabled={isDeclinePending}
                  onPress={onNewBillingMethod}
                  testId={addPaymentMethodTestId}
                />
              ) : (
                <Button
                  label="Accept"
                  variant="default"
                  isSkeleton={isSkeleton}
                  isDisabled={isDeclinePending}
                  isPending={isAcceptPending}
                  onPress={onAccept}
                  testId={acceptTestId}
                />
              )}
              <Button
                label="Decline"
                variant="outline"
                isSkeleton={isSkeleton}
                isDisabled={isAcceptPending}
                isPending={isDeclinePending}
                onPress={onDecline}
                testId={declineTestId}
              />
            </>
          )}
          <Button
            label={status === 'PendingClientApproval' ? 'Not now' : 'Close'}
            variant={status === 'PendingClientApproval' ? 'text' : 'outline'}
            isSkeleton={isSkeleton}
            isDisabled={isAcceptPending || isDeclinePending}
            onPress={onClose}
            testId={
              status === 'PendingClientApproval' ? notNowTestId : closeTestId
            }
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface JobScopeChangeDialogContentProps {
  header?: ReactNode
  children?: ReactNode
}

/**
 * Job Scope Change Dialog Content
 */
export function JobScopeChangeDialogContent({
  children,
  header,
}: JobScopeChangeDialogContentProps) {
  const { connectRef, scrollYStart, scrollYEnd } = useScrollOffsets()
  const { maskImage } = useScrollFadeMaskImage({
    scrollYStart,
    scrollYEnd,
    fade: 'bottom',
    fadeSegmentHeight: 'var(--spacing-5xl)',
    inputScrollRange: [5, 20],
  })

  return (
    <div className={styles.content}>
      <m.div className={styles.scroller} ref={connectRef} style={{ maskImage }}>
        {header}
        <div className={styles.tableContainer}>{children}</div>
      </m.div>
    </div>
  )
}

interface JobScopeChangeDeclineDialogProps {
  /**
   * Form ID to submit.
   */
  form: string
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Whether the decline operation is pending.
   */
  isPending: boolean
  /**
   * Content to display in the dialog.
   */
  children?: ReactNode
  /**
   * Called when the dialog is closed.
   */
  onClose: () => unknown
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the go back button.
   */
  goBackTestId?: string
  /**
   * Test ID attribute for the decline changes button.
   */
  declineChangesTestId?: string
}

/**
 * The JobScopeChangeDeclineDialog
 */
export function JobScopeChangeDeclineDialog({
  form,
  isOpen,
  isPending,
  children,
  onClose,
  testId,
  goBackTestId,
  declineChangesTestId,
}: JobScopeChangeDeclineDialogProps) {
  return (
    <ModalDialog
      isUsingDialogScrolling
      size="base"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          Are you sure you want to decline these changes?
        </ModalDialogTitle>
        <ModalDialogDescription>
          These items are being requested in order for your Pro to file an
          accurate return. If declined, this could impact your taxes being IRS
          compliant and could result in your job being unable to be completed by
          your Pro.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter bordered compact sticky>
        <ButtonGroup stretch>
          <Button
            label="Go back"
            variant="outline"
            onPress={onClose}
            testId={goBackTestId}
          />
          <Button
            form={form}
            label="Decline changes"
            variant="default"
            isPending={isPending}
            type="submit"
            testId={declineChangesTestId}
          />
        </ButtonGroup>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface JobScopeChangeApprovedDialogProps {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Called when the dialog is closed.
   */
  onClose: () => unknown
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the OK button.
   */
  okTestId?: string
}

/**
 * The JobScopeChangeApprovedDialog
 */
export function JobScopeChangeApprovedDialog({
  isOpen,
  onClose,
  testId,
  okTestId,
}: JobScopeChangeApprovedDialogProps) {
  return (
    <ModalDialog size="base" isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>You have accepted these changes</ModalDialogTitle>
        <ModalDialogDescription>
          Thank you for your response. We have added these additional services.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Ok"
            variant="default"
            onPress={onClose}
            testId={okTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface PendingApprovalHeroProps {
  amountDue: number | null
  isSkeleton: boolean
}

/**
 * Pending Approval Hero
 */
export function PendingApprovalHero({
  isSkeleton,
  amountDue,
}: PendingApprovalHeroProps) {
  if (!amountDue) {
    return null
  }

  return (
    <ModalDialogHero variant="default" isSkeleton={isSkeleton}>
      <DialogHero>
        <div className={styles.text}>
          <AutoSkeleton isSkeleton={isSkeleton}>
            Additional amount due
          </AutoSkeleton>
        </div>
        <div className={styles.price}>
          <AutoSkeleton isSkeleton={isSkeleton}>
            {formatMoney(amountDue)}
          </AutoSkeleton>
        </div>
      </DialogHero>
    </ModalDialogHero>
  )
}

interface PendingApprovalHeaderProps {
  price: number | null
  isSkeleton: boolean
}

/**
 * The PendingApprovalHeader
 */
export function PendingApprovalHeader({
  price,
  isSkeleton,
}: PendingApprovalHeaderProps) {
  return (
    <ModalDialogHeader>
      <ModalDialogTitle>
        <AutoSkeleton isSkeleton={isSkeleton}>
          New services identified
        </AutoSkeleton>
      </ModalDialogTitle>

      <ModalDialogDescription>
        <AutoSkeleton isSkeleton={isSkeleton}>
          We are unable to proceed with your return until you acknowledge these
          updates.
          {!!price && (
            <>
              {' '}
              By accepting these changes, the new total will be{' '}
              <span className={styles.dialogPrice}>{formatMoney(price)}</span>
            </>
          )}
        </AutoSkeleton>
      </ModalDialogDescription>
    </ModalDialogHeader>
  )
}

interface RejectedHeroProps {
  amountDue: number | null
  isSkeleton: boolean
}

/**
 * Rejected Hero
 */
export function RejectedHero({ isSkeleton, amountDue }: RejectedHeroProps) {
  if (!amountDue) {
    return null
  }

  return (
    <ModalDialogHero variant="warning" isSkeleton={isSkeleton}>
      <DialogHero>
        <div className={styles.text}>
          <AutoSkeleton isSkeleton={isSkeleton}>
            Additional amount declined
          </AutoSkeleton>
        </div>
        <div className={styles.price}>
          <AutoSkeleton isSkeleton={isSkeleton}>
            {formatMoney(amountDue)}
          </AutoSkeleton>
        </div>
      </DialogHero>
    </ModalDialogHero>
  )
}

interface RejectedHeaderProps {
  displayName: string
  respondedAt: Date | null
  isSkeleton: boolean
}

/**
 * The RejectedHeader
 */
export function RejectedHeader({
  displayName,
  respondedAt,
  isSkeleton,
}: RejectedHeaderProps) {
  return (
    <ModalDialogHeader>
      <ModalDialogTitle>
        <AutoSkeleton characters={30} isSkeleton={isSkeleton}>
          Declined service updates
        </AutoSkeleton>
      </ModalDialogTitle>
      <ModalDialogDescription>
        <AutoSkeleton isSkeleton={isSkeleton}>
          You rejected all updates identified by your Pro, {displayName}, on{' '}
          {respondedAt && dateFormatters.dateTimeDefault.format(respondedAt)}.
        </AutoSkeleton>
      </ModalDialogDescription>
    </ModalDialogHeader>
  )
}

interface ApprovedHeroProps {
  amountDue: number | null
  isSkeleton: boolean
}

/**
 * Approved Hero
 */
export function ApprovedHero({ isSkeleton, amountDue }: ApprovedHeroProps) {
  if (!amountDue) {
    return null
  }

  return (
    <ModalDialogHero variant="success" isSkeleton={isSkeleton}>
      <DialogHero>
        <div className={styles.text}>
          <AutoSkeleton isSkeleton={isSkeleton}>
            Additional amount approved
          </AutoSkeleton>
        </div>
        <AutoSkeleton isSkeleton={isSkeleton}>
          <div className={styles.price}>{formatMoney(amountDue)}</div>
        </AutoSkeleton>
      </DialogHero>
    </ModalDialogHero>
  )
}

interface ApprovedHeaderProps {
  displayName: string
  respondedAt: Date | null
  isSkeleton: boolean
}

/**
 * The ApprovedHeader
 */
export function ApprovedHeader({
  displayName,
  respondedAt,
  isSkeleton,
}: ApprovedHeaderProps) {
  return (
    <ModalDialogHeader>
      <ModalDialogTitle>
        <AutoSkeleton characters={30} isSkeleton={isSkeleton}>
          Accepted service updates
        </AutoSkeleton>
      </ModalDialogTitle>
      <ModalDialogDescription>
        <AutoSkeleton isSkeleton={isSkeleton}>
          You approved all updates identified by your Pro, {displayName}, on{' '}
          {respondedAt && dateFormatters.dateTimeDefault.format(respondedAt)}
        </AutoSkeleton>
      </ModalDialogDescription>
    </ModalDialogHeader>
  )
}

interface JobScopeChangeTableProps {
  children?: ReactNode
  withoutPrice?: boolean
  testId?: string
}

/**
 * The JobScopeChangeTable
 */
export function JobScopeChangeTable({
  children,
  withoutPrice,
  testId,
}: JobScopeChangeTableProps) {
  return (
    <Table
      variant="dialog"
      rowSpacing="lg"
      aria-label="Service items"
      testId={testId}
    >
      <TableHeader sticky={0}>
        <TableColumn isRowHeader>Service item</TableColumn>
        <TableColumn align="right">Quantity</TableColumn>
        {!withoutPrice && <TableColumn align="right">Amount</TableColumn>}
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  )
}

interface JobScopeChangeItemRowProps {
  title: string
  description: string
  quantity: number
  price?: number | null
  isBundle?: boolean
  isIncluded?: boolean
}

/**
 * The JobScopeChangeItemRow
 */
export function JobScopeChangeItemRow({
  title,
  description,
  quantity,
  price,
  isBundle = false,
  isIncluded = false,
}: JobScopeChangeItemRowProps) {
  const displayDescription = isBundle
    ? description
      ? `Bundle. ${description}`
      : 'Bundle.'
    : description

  return (
    <TableRow isHighlighted={isBundle}>
      <TableCell>
        <div className={styles.dialogItemTitle}>{title}</div>
        <div className={styles.dialogItemDescription}>{displayDescription}</div>
      </TableCell>
      <TableCell align="right">
        <div className={styles.dialogItemText}>
          {formatJobScopeChangeItemQuantity(quantity)}
        </div>
      </TableCell>
      {isNotNil(price) && (
        <TableCell align="right">
          <div
            className={cx(
              styles.dialogItemText,
              isIncluded && styles.dialogItemIncluded,
            )}
          >
            {isIncluded && price === 0
              ? 'Included'
              : formatJobScopeChangeItemPrice(price ?? 0)}
          </div>
        </TableCell>
      )}
    </TableRow>
  )
}

interface DialogHeaderProps {
  children: ReactNode
}

/**
 * The DialogHero
 */
export function DialogHero({ children }: DialogHeaderProps) {
  return <div className="flex flex-col gap-xs">{children}</div>
}

interface SkeletonTableProps {
  count?: number
}

/**
 * The SkeletonTable
 */
export function SkeletonTable({ count }: SkeletonTableProps) {
  return (
    <Table variant="dialog" rowSpacing="lg" aria-label="Service items">
      <TableHeader>
        <TableColumn isSkeleton isRowHeader>
          Service item
        </TableColumn>
        <TableColumn isSkeleton align="right">
          Quantity
        </TableColumn>
        <TableColumn isSkeleton align="right">
          Amount
        </TableColumn>
      </TableHeader>
      <TableBody>
        <SkeletonList count={count}>
          <TableRowSkeleton>
            <TableCellSkeleton>
              <div className={styles.dialogItemTitle}>
                <AutoSkeleton>Name</AutoSkeleton>
              </div>

              <div className={styles.dialogItemDescription}>
                <AutoSkeleton>Description</AutoSkeleton>
              </div>
            </TableCellSkeleton>
            <TableCellSkeleton align="right">
              <div className={styles.dialogItemText}>
                <AutoSkeleton>1</AutoSkeleton>
              </div>
            </TableCellSkeleton>
            <TableCellSkeleton align="right">
              <div className={styles.dialogItemText}>
                <AutoSkeleton>+$30</AutoSkeleton>
              </div>
            </TableCellSkeleton>
          </TableRowSkeleton>
        </SkeletonList>
      </TableBody>
    </Table>
  )
}
